import Question, { QuestionProps } from '@/modules/chatbot/components/Question';
import {
  AMPLITUDE_ACTIONS,
  AMPLITUDE_EVENTS,
} from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import findNextQuestionToAsk from '@/modules/core/chatbot/findNextQuestionToAsk';
import { ChatbotQuestions } from '@/modules/core/chatbot/getQuestionsToAsk';
import { shouldAnimateCurrentQuestion } from '@/modules/core/chatbot/shouldAnimateCurrentQuestion';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import UpdatedQuestion from '@/modules/core/data/types/CDE/UpdatedQuestion';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { ModChatbot } from '@/modules/core/data/types/ModChatbot';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { ReactSelectDefaultOption } from '@/modules/core/reactSelect/types/ReactSelect';
import Icon from '@/common/components/dataDisplay/Icon';
import { css } from '@emotion/react';
import { XCircle } from 'lucide-react';
import classnames from 'classnames';
import filter from 'lodash.filter';
import find from 'lodash.find';
import get from 'lodash.get';
import map from 'lodash.map';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
} from 'react';

type Props = {
  questions: ChatbotQuestions;
  activeQuestion: ChatbotQuestion;
  context: ChatbotContext;
  setActiveQuestion: Dispatch<SetStateAction<ChatbotQuestion>>;
  setContext: Dispatch<SetStateAction<ChatbotContext>>;
  updatedQuestion: UpdatedQuestion;
  setUpdatedQuestion: Dispatch<SetStateAction<UpdatedQuestion>>;
  updatedQuestionAnimationDuration: number;
  logEvent: LogEvent;
  bubbleTimerDuration: number;
  allEducationalPrograms: AirtableRecord<EducationalProgram>[];
  allEducationalProgramSteps: AirtableRecord<EducationalProgramStep>[];
  allCampuses: AirtableRecord<Campus>[];
  allStudentSolutionsCategories: AirtableRecord<StudentSolutionsCategory>[];
  modChatbot: AirtableRecord<ModChatbot>;
}

/**
 * Question about the user's persona (student, parent, etc.)
 *
 * Always displayed first.
 */
const PersonaQuestion: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    questions,
    activeQuestion,
    context,
    setActiveQuestion,
    setContext,
    updatedQuestion,
    setUpdatedQuestion,
    updatedQuestionAnimationDuration,
    logEvent,
    bubbleTimerDuration,
    allEducationalPrograms,
    allEducationalProgramSteps,
    allCampuses,
    allStudentSolutionsCategories,
    modChatbot,
  } = props;
  const {
    personaQuestion,
    programsQuestion,
    programStepsQuestion,
  } = questions;

  return (
    <Question
      question={personaQuestion}
      activeQuestion={activeQuestion}
      context={context}
      // XXX Bypass the "bubbleTimer" when the active question isn't "persona", because that means we're loading the chatbot while already knowing the user's persona
      bubbleTimerDuration={activeQuestion === personaQuestion ? bubbleTimerDuration : 0}
    >
      {
        (props: QuestionProps): JSX.Element => {
          const {
            question,
          } = props;

          return (
            <Fragment>
              <div
                className={'message'}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: question.label,
                  }}
                />
                <div
                >
                  {
                    map(filter(question.choices, (choice: { value: string }): boolean => {
                      // Filter out the selected choice, when a choice is selected
                      return choice.value !== context[question.ref];
                    }), (choice: ReactSelectDefaultOption, i: number) => {
                      const { value, label } = choice;

                      return (
                        <button
                          key={i}
                          className={classnames('option-button')}
                          onClick={(): void => {
                            const nextContext: ChatbotContext = {
                              ...context,
                              [question.ref]: value,
                            };
                            const nextQuestionToAsk = findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, modChatbot, setContext);

                            // When the persona is changed after another question was asked, we don't want to hide the questions that were asked/answered after the "persona" question
                            // Because the persona question doesn't affect the workflow of other questions, so it shouldn't "reset" the context, not affect the currently asked question
                            // In this case, we want to keep the current active question as active (no UI change), while updating the context (to update the persona)
                            if (activeQuestion.order > question.order) {
                              setContext(nextContext); // Another question is displayed, keep it displayed while updating the persona
                            } else {
                              setActiveQuestion(nextQuestionToAsk);
                              setContext(nextContext);
                            }

                            // Handling animation of other questions that need to be "refreshed" when this answer changes
                            setUpdatedQuestion(question);
                            setTimeout(() => {
                              setUpdatedQuestion(null);
                            }, updatedQuestionAnimationDuration);

                            logEvent(AMPLITUDE_EVENTS.CHATBOT_ANSWER, {
                              question: {
                                ref: question.ref,
                                inputType: 'quick-replies',
                                isMultiChoices: false,
                                rank: question.order,
                              },
                              selection: {
                                ref: value,
                              },
                              action: AMPLITUDE_ACTIONS.SELECT,
                            });
                          }}
                          dangerouslySetInnerHTML={{ __html: label }}
                        />
                      );
                    })
                  }
                </div>
              </div>
              {
                context[question.ref] && (
                  <div
                    className={classnames('message user', { [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, question) })}
                  >
                    <span
                      // @ts-ignore
                      dangerouslySetInnerHTML={{ __html: get(find(question.choices, { value: context[question.ref] }), 'label') }}
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      className={'close-message'}
                      css={css`
                        cursor: pointer;
                      `}
                      onClick={(): void => {
                        const nextContext: ChatbotContext = {
                          ...context,
                          [question.ref]: undefined,

                          // Also reset the answers given to the next questions (to avoid state mismatch)
                          [programsQuestion.ref]: undefined,
                          [programStepsQuestion.ref]: undefined,
                        };
                        setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, modChatbot, setContext));

                        logEvent(AMPLITUDE_EVENTS.CHATBOT_ANSWER, {
                          question: {
                            ref: question.ref,
                            inputType: 'quick-replies',
                            isMultiChoices: false,
                            rank: question.order,
                          },
                          selection: {
                            ref: context[question.ref]?.value,
                          },
                          action: AMPLITUDE_ACTIONS.REMOVE,
                        });
                      }}
                    >
                      <Icon icon={XCircle} />
                    </span>
                  </div>
                )
              }
            </Fragment>
          );
        }
      }
    </Question>
  );
};

export default PersonaQuestion;
